import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUserWithActiveAddress } from "../../State/Auth/Action";
import AuthModal from "../../Auth/AuthModal";
import { getCart } from "../../State/Cart/Action";
import { getAllCategory } from "../../State/Category/Action";
import { getWishlist } from "../../State/Wishlist/Action";
import { getAllProducts } from "../../State/Product/Action";
import { useCurrency } from "../../../config/CurrencyContext";
import defaultImage from "../../../Common/productDefaultImage.png";
import SearchSuggestPanel from "./SearchSuggestPanel";

const packMenuColumns = (sections, columnCount = 3) => {
  const columns = Array.from({ length: columnCount }, () => []);
  const weights = Array(columnCount).fill(0);
  sections.forEach((section) => {
    const index = weights.indexOf(Math.min(...weights));
    columns[index].push(section);
    weights[index] += 2 + (section.productCategories?.length || 0);
  });
  return columns.filter((column) => column.length > 0);
};

const uniqueFeaturedCards = (sections) => {
  const seen = new Set();
  return sections
    .flatMap((sub) =>
      (sub.productCategories || []).map((productCategory) => ({
        ...productCategory,
        subCategoryName: sub.name,
      }))
    )
    .filter((item) => {
      const image = item.image;
      if (!image || image === defaultImage || seen.has(image) || seen.has(item.name)) {
        return false;
      }
      seen.add(image);
      seen.add(item.name);
      return true;
    })
    .slice(0, 3);
};

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const [openAuthModal, setOpenAuthModal] = useState(false);
  const jwt = localStorage.getItem("jwt");
  const role = localStorage.getItem("role");
  const navigate = useNavigate();
  const { auth, cart, publicCategory, wishlist, products } = useSelector(
    (store) => store
  );
  const dispatch = useDispatch();
  const location = useLocation();

  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [panelTop, setPanelTop] = useState(72);
  const headerTopRef = useRef(null);
  const searchPanelRef = useRef(null);
  const searchInputRef = useRef(null);
  const { currencies, selectedCurrency, changeCurrency } = useCurrency();

  const extractProductList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.response)) return payload.response;
    if (Array.isArray(payload?.response?.products)) return payload.response.products;
    if (Array.isArray(payload?.content)) return payload.content;
    return [];
  };
  const catalogProducts = extractProductList(products?.allProducts).length
    ? extractProductList(products.allProducts)
    : extractProductList(products?.products);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const currentQuery = searchParams.get("query");

    if (location.pathname.startsWith("/products/search")) {
      if (!currentQuery || currentQuery.trim() === "") {
        // Reset URL with empty query and state
        navigate("/products/search?query=", { replace: true });
        setQuery("");
      } else {
        setQuery(currentQuery);
      }
    } else {
      // On any other path, clear local query state
      setQuery("");
    }
  }, [location.pathname, location.search, navigate]);



  const closeSearch = () => setSearchOpen(false);

  const openSearch = () => {
    setSearchOpen(true);
    if (!catalogProducts.length) {
      dispatch(getAllProducts());
    }
  };

  const goToSearchResults = (term) => {
    const value = (term ?? query).trim();
    closeSearch();
    navigate(`/products/search?query=${encodeURIComponent(value)}`);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      goToSearchResults(query);
    }
    if (e.key === "Escape") {
      closeSearch();
      searchInputRef.current?.blur();
    }
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
  };

  useEffect(() => {
    if (!searchOpen) return;
    const top = headerTopRef.current?.getBoundingClientRect().bottom;
    if (top) setPanelTop(top + 8);

    const onPointerDown = (event) => {
      const inPanel = searchPanelRef.current?.contains(event.target);
      const inInput = searchInputRef.current?.contains(event.target);
      if (!inPanel && !inInput) closeSearch();
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [searchOpen]);

  useEffect(() => {
    closeSearch();
  }, [location.pathname]);

  // Handles user menu click
  const handleUserClick = (event) => {
    // setAnchorEl(event.currentTarget);
    navigate("/account")
  };

  // Opens login modal and navigates to login
  const handleOpenAuthModal = () => {
    setOpenAuthModal(true);
  };

  // Closes login modal
  const handleCloseAuthModal = () => {
    setOpenAuthModal(false);
  };

  const handleWhatsAppClick = () => {
    const username = auth?.user?.name || auth?.user?.email || 'Guest';
    const message = `Hi there I am ${username}, I need help on inarifashion.com`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/918076834935?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  // Handles navigation for categories
  const handleCategoryClick = (category, section, item) => {
    if (section.name.toLowerCase() === "designers") {
      navigate(`/collections/brand/${item.name}`);
    } else {
      navigate(`/${category}/${section.name}/${item.name}`);
    }
  };

  const handleFavoriteClick = () => {
    if (jwt && role === "CUSTOMER") {
      navigate("/account/wishlist");
    } else {
      handleOpenAuthModal();
    }
  };

  const handleCartClick = () => {
    if (jwt && role === "CUSTOMER") {
      navigate("/cart");
    } else {
      handleOpenAuthModal();
    }
  };

  // Fetch all categories, user data, and cart items if `jwt` is available
  useEffect(() => {
    dispatch(getAllCategory());
    if (jwt && role === "CUSTOMER") {
      dispatch(getUserWithActiveAddress());
      dispatch(getCart());
      dispatch(getWishlist());
    }
  }, [jwt, dispatch, cart.addCartItem, cart.deleteCartItem, cart.updateCartItem, role]); // Added `dispatch` for best practices

  // Handle redirection based on user role and authentication state
  useEffect(() => {
    if (auth.user && jwt) {
      handleCloseAuthModal();

      // Navigate back if on authentication-related pages
      const authPaths = ["/login", "/register", "/vendorRegister"];
      if (authPaths.includes(location.pathname)) navigate(-1);
    }
  }, [auth.user, location.pathname, navigate, jwt]); // Added `navigate` and `location.pathname` for dependency completeness

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {searchOpen && (
        <div
          className="fixed inset-0 z-[45] bg-black/40"
          onMouseDown={closeSearch}
          aria-hidden="true"
        />
      )}
      {searchOpen && (
        <div
          ref={searchPanelRef}
          className="fixed left-1/2 z-[55] w-[min(1080px,calc(100vw-24px))] -translate-x-1/2"
          style={{ top: panelTop }}
        >
          <div className="flex h-[min(78vh,740px)] flex-col overflow-hidden rounded-2xl bg-white p-5 shadow-[0_18px_50px_rgba(0,0,0,0.16)] sm:p-6">
            <SearchSuggestPanel
              query={query}
              categories={publicCategory.categories?.response || []}
              products={catalogProducts}
              onSearch={goToSearchResults}
              onOpenProduct={closeSearch}
              onNeedAuth={handleOpenAuthModal}
            />
          </div>
        </div>
      )}
      <div className="relative z-[60] border-b border-stone-100 bg-white" ref={headerTopRef}>
      <div className="relative mx-auto max-w-7xl px-3 sm:px-4">
        {/* Top Row - Currency, Brand, Search, and Utility Icons */}
        <div className="flex items-center justify-between gap-2 py-3.5">
          {/* Left Side - Currency Selector and Brand Name */}
          <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
            <button
              onClick={toggleMobileMenu}
              className="md:hidden bg-transparent text-stone-700 p-1 shadow-none"
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Link to="/" className="brand-wordmark hover:opacity-80" aria-label="Inari home">
              inari
            </Link>

            {currencies?.length > 0 && (
              <select
                className="hidden sm:block bg-transparent border-none text-[13px] font-normal text-[#333] focus:outline-none cursor-pointer"
                value={selectedCurrency}
                onChange={(e) => changeCurrency(e.target.value)}
              >
                {currencies?.map((c) => (
                  <option key={c.currencyCode} value={c.currencyCode}>{c.currencyCode}</option>
                ))}
              </select>
            )}
          </div>

          {/* Center - Search Box */}
          <div className="mx-1 min-w-[60px] flex-1 sm:mx-4 sm:min-w-[150px] md:min-w-[360px] lg:max-w-xl">
            <div className="relative">
              <svg className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-[#9a9a9a]" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path strokeLinecap="round" d="M20 20l-3.2-3.2" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search for products, brands and more"
                className="w-full rounded-full border-0 bg-[#f4f1ee] py-2 pl-10 pr-4 text-xs text-[#333] placeholder-[#9a9a9a] focus:outline-none focus:ring-1 focus:ring-[#d8d2cc] md:text-[13px]"
                value={query}
                onChange={handleChange}
                onFocus={openSearch}
                onClick={openSearch}
                onKeyDown={handleSearchKeyDown}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Right Side - Utility Icons */}
          <div className="flex flex-shrink-0 items-center gap-0.5 sm:gap-1">
            <button
              className="bg-transparent p-1.5 text-[#333] shadow-none hover:text-[#BB3D1F]"
              onClick={handleWhatsAppClick}
              aria-label="WhatsApp"
            >
              <svg className="h-[22px] w-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12a7.5 7.5 0 01-11.3 6.5L4.5 19.5l1.1-3.5A7.5 7.5 0 1119.5 12z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.4 10.4c.7 2 2.2 3.5 4.2 4.2.3.1.6 0 .8-.2l.7-.7c.2-.2.5-.2.7-.1l1.4.6c.3.1.4.4.3.7l-.2.8c-.1.5-.6.8-1.1.7-3.3-.7-5.9-3.3-6.6-6.6-.1-.5.2-1 .7-1.1l.8-.2c.3-.1.6 0 .7.3l.6 1.4c.1.2.1.5-.1.7l-.7.7c-.2.2-.3.5-.2.8z" />
              </svg>
            </button>

            <button
              className="relative bg-transparent p-1.5 text-[#333] shadow-none hover:text-[#BB3D1F]"
              onClick={handleFavoriteClick}
              aria-label="Wishlist"
            >
              <svg className="h-[22px] w-[22px]" fill="none" stroke="currentColor" strokeWidth="1.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 20s-7-4.4-7-10a4 4 0 017-2.6A4 4 0 0119 10c0 5.6-7 10-7 10z" />
              </svg>
              <span className="absolute -right-0.5 -top-0.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-brand px-0.5 text-[9px] font-medium leading-none text-white">
                {(auth.user && wishlist.wishlistItems?.length) || 0}
              </span>
            </button>
            <button className="bg-transparent p-1.5 text-[#333] shadow-none hover:text-[#BB3D1F]" aria-label="Account">
              {auth.user && auth?.user?.role === "CUSTOMER" ? (
                <span
                  className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-brand text-[11px] font-medium text-white"
                  onClick={handleUserClick}
                >
                  {auth?.user?.name?.charAt(0)?.toUpperCase() ||
                  auth?.user?.email?.charAt(0)?.toUpperCase() ||
                  "U"}
                </span>
              ) : (
                <svg className="h-[22px] w-[22px]" fill="none" stroke="currentColor" strokeWidth="1.4" viewBox="0 0 24 24" onClick={handleOpenAuthModal}>
                  <circle cx="12" cy="8" r="3.25" />
                  <path strokeLinecap="round" d="M5.5 19.2c.8-3 3.3-4.7 6.5-4.7s5.7 1.7 6.5 4.7" />
                </svg>
              )}
            </button>
            <button
              className="relative bg-transparent p-1.5 text-[#333] shadow-none hover:text-[#BB3D1F]"
              onClick={handleCartClick}
              aria-label="Bag"
            >
              <svg className="h-[22px] w-[22px]" fill="none" stroke="currentColor" strokeWidth="1.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 8.5h12l.8 11.5H5.2L6 8.5z" />
                <path strokeLinecap="round" d="M9 8.5V7a3 3 0 016 0v1.5" />
              </svg>
              <span className="absolute -right-0.5 -top-0.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-brand px-0.5 text-[9px] font-medium leading-none text-white">
                {(auth.user && (cart?.isBuyNowCheckout ? cart?.bagItemCount : cart?.cartItems?.length)) || 0}
              </span>
            </button>
          </div>
        </div>
      </div>

        {mobileMenuOpen && !searchOpen && (
          <div className="md:hidden bg-white border-t border-stone-100 py-4 max-h-[70vh] overflow-y-auto">
            <div className="px-4 space-y-5">
              {publicCategory.categories?.response?.map((item) => (
                <div key={item.name} className="pb-4 border-b border-stone-100 last:border-b-0">
                  <Link
                    to={`/${item.name}`}
                    className="block py-1 text-[14px] leading-5 font-normal text-[#333333]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                  {item.subCategories?.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4">
                      {item.subCategories.map((subCategory) => (
                        <div key={subCategory.name}>
                          <div className="text-[14px] leading-5 font-normal text-[#333333] mb-1">
                            {subCategory.name}
                          </div>
                          {subCategory.productCategories?.slice(0, 8).map((productCategory) => (
                            <Link
                              key={productCategory.name}
                              to={`/${item.name}/${subCategory.name}/${productCategory.name}`}
                              className="block py-2 text-[14px] leading-5 font-light text-[#333333] hover:text-brand"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {productCategory.name}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <nav className={`relative hidden md:block ${searchOpen ? "!hidden" : ""}`}>
          <ul className="max-w-[1440px] mx-auto flex items-center justify-center list-none m-0 px-8 xl:px-14 pt-0 pb-0">
            {publicCategory.categories?.response?.map((item) => {
              const menuSections = (item.subCategories || []).filter(
                (section) => section.productCategories?.length > 0
              );
              const menuColumns = packMenuColumns(menuSections);
              const featuredCards = uniqueFeaturedCards(menuSections);
              const isActive = location.pathname === "/" + item.name;

              return (
                <li key={item.name} className="static group">
                  <Link
                    to={`/${item.name}`}
                    className={`relative block px-3 pb-2 font-sans text-[14px] leading-5 font-normal no-underline ${
                      isActive ? "text-brand" : "text-[#333333] group-hover:text-brand"
                    }`}
                  >
                    {item.name}
                    <span className={`absolute left-3 right-3 bottom-0 h-[2px] ${isActive ? "bg-brand" : "bg-transparent group-hover:bg-brand"}`} />
                  </Link>

                  {menuSections.length > 0 && (
                    <div className="invisible opacity-0 pointer-events-none group-hover:visible group-hover:opacity-100 group-hover:pointer-events-auto absolute inset-x-0 top-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.06)] border-t border-stone-100 z-50">
                      <div className="max-w-[1440px] mx-auto px-8 xl:px-14 py-0">
                        <div className="grid grid-cols-6 gap-2">
                          {[0, 1, 2].map((columnIndex) => (
                            <div
                              key={`col-${columnIndex}`}
                              className="pt-4 pl-4 odd:bg-[#e8e8e8]/20 min-h-[220px]"
                            >
                              <div className="space-y-5">
                                {(menuColumns[columnIndex] || []).map((section) => (
                                  <div key={section.name}>
                                    <p className="m-0 mb-1 font-sans text-[14px] leading-5 font-normal text-[#333333]">
                                      {section.name}
                                    </p>
                                    <ul className="list-none m-0 p-0">
                                      {section.productCategories.slice(0, 10).map((subItem) => (
                                        <li key={subItem.name} className="m-0 p-0">
                                          <button
                                            type="button"
                                            className="bg-transparent border-0 text-left font-sans text-[14px] leading-5 font-light text-[#333333] hover:text-brand hover:font-normal py-2 px-0"
                                            onClick={() => handleCategoryClick(item.name, section, subItem)}
                                          >
                                            {subItem.name}
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}

                          {[0, 1, 2].map((cardIndex) => {
                            const product = featuredCards[cardIndex];
                            if (!product) {
                              return <div key={`card-empty-${cardIndex}`} />;
                            }
                            return (
                              <button
                                type="button"
                                key={`${product.subCategoryName}-${product.name}`}
                                className="bg-transparent p-0 border-0 text-center"
                                onClick={() => {
                                  if (product.subCategoryName?.toLowerCase() === "designers") {
                                    navigate(`/collections/brand/${product.name}`);
                                  } else {
                                    navigate(`/${item.name}/${product.subCategoryName}/${product.name}`);
                                  }
                                }}
                              >
                                <div className="aspect-[3/4] overflow-hidden bg-[#f4f4f4]">
                                  <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-full h-full object-cover object-top"
                                  />
                                </div>
                                <div className="flex flex-col items-center w-full py-3 bg-white">
                                  <p className="m-0 mb-1 font-sans text-[14px] leading-5 font-semibold text-[#333333] truncate w-full">
                                    {product.name}
                                  </p>
                                  <small className="font-sans text-xs leading-4 font-normal text-[#333333]">
                                    SHOP NOW
                                  </small>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      <AuthModal handleClose={handleCloseAuthModal} open={openAuthModal} />
      </div>
    </div>
  );
}
